/**
 * Comparison report header — displays the two restaurants being compared
 * side-by-side at the top of a comparison FIND.
 *
 *   COMPARISON REPORT
 *   IpponYari · Izakaya Tago   vs   Botejyu · Alabang Town Center
 *
 * Detection is layered (newest wins):
 *
 *   1. Survey config — when the survey is flagged `is_comparison: true`
 *      and the four `comparison_question_ids` fields are set, those IDs
 *      are used. This is the future-proof, platform-admin-driven path.
 *
 *   2. Legacy hardcoded IDs — the four production IpponYari comparison
 *      question_ids. Kept so existing FINDs (RESP-20260625-R7DCE et al.)
 *      keep rendering correctly with no survey-config migration.
 *
 *   3. Neither path matches → returns null. Existing single-branch
 *      headers are completely unaffected.
 *
 * All reads are pure overlays on `answers` + optional `qa_edits[]`. No
 * data is written anywhere.
 */
import React from 'react';

// Production IpponYari comparison survey IDs (verified by the user).
export const LEGACY_COMPARISON_IDS = {
  r1_brand:  '83647fcf-aae2-4079-9e57-628a280b2a48',
  r1_branch: 'e5ebed7a-b9a7-4712-8142-e6d3613c74bc',
  r2_brand:  '743119a2-e03c-42be-ade6-4bf887255841',
  r2_branch: '2a7006c6-1436-4fb2-80c6-c0f7eaaa87b0',
};

function isMeaningful(v) {
  if (v == null) return false;
  if (typeof v === 'string') return v.trim() !== '';
  if (Array.isArray(v)) return v.length > 0;
  if (typeof v === 'object') {
    if (typeof v.transcription === 'string' && v.transcription.trim()) return true;
    if (typeof v.label === 'string' && v.label.trim()) return true;
    if (typeof v.text === 'string' && v.text.trim()) return true;
    if (v.value != null && String(v.value).trim() !== '') return true;
    return false;
  }
  return true;
}

function valueToString(raw) {
  if (raw == null) return '';
  if (typeof raw === 'string') return raw.trim();
  if (typeof raw === 'number') return String(raw);
  if (Array.isArray(raw)) {
    return raw
      .map(x => (x && typeof x === 'object')
        ? (x.label || x.text || x.transcription || (x.value != null ? String(x.value) : ''))
        : String(x))
      .filter(Boolean)
      .join(', ');
  }
  if (typeof raw === 'object') {
    if (raw.transcription) return String(raw.transcription).trim();
    if (raw.label)         return String(raw.label).trim();
    if (raw.text)          return String(raw.text).trim();
    if (raw.value != null) return String(raw.value).trim();
  }
  return String(raw).trim();
}

/** Resolve a single answer for a question, applying qa_edits overlay. */
export function resolveAnswer(answers, qaEdits, qid) {
  if (!qid) return '';
  // The QA edit, if present for this question, ALWAYS wins (text + selection
  // edits live in the same overlay collection — last-write-wins per qid).
  const edit = (qaEdits || []).find(e => e && e.question_id === qid);
  if (edit && isMeaningful(edit.edited_answer)) {
    return valueToString(edit.edited_answer);
  }
  return valueToString(answers?.[qid]);
}

/**
 * Decide which set of comparison question IDs applies — survey config first,
 * then the legacy hardcoded set. Returns `null` when the FIND isn't a
 * comparison report at all.
 */
export function resolveComparisonIds(answers, surveyConfig) {
  // 1) Survey-config-driven (preferred).
  const cfg = surveyConfig?.comparison_question_ids;
  if (
    surveyConfig?.is_comparison === true &&
    cfg && cfg.r1_brand && cfg.r1_branch && cfg.r2_brand && cfg.r2_branch
  ) {
    return {
      r1_brand:  cfg.r1_brand,
      r1_branch: cfg.r1_branch,
      r2_brand:  cfg.r2_brand,
      r2_branch: cfg.r2_branch,
    };
  }
  // 2) Legacy fallback — match only when ALL four legacy IDs have answers.
  const a = answers || {};
  const legacyComplete = Object.values(LEGACY_COMPARISON_IDS).every(
    qid => isMeaningful(a[qid]),
  );
  if (legacyComplete) return { ...LEGACY_COMPARISON_IDS };
  return null;
}

/**
 * Render the comparison header block. Returns null when the FIND isn't
 * a comparison report — safe to drop in unconditionally.
 */
export default function ComparisonHeader({
  answers,
  qaEdits,
  surveyConfig,
  variant = 'card',      // 'card' for report / response-detail; 'inline' for qa-review header line
  className = '',
}) {
  const ids = resolveComparisonIds(answers, surveyConfig);
  if (!ids) return null;

  const r1Brand  = resolveAnswer(answers, qaEdits, ids.r1_brand);
  const r1Branch = resolveAnswer(answers, qaEdits, ids.r1_branch);
  const r2Brand  = resolveAnswer(answers, qaEdits, ids.r2_brand);
  const r2Branch = resolveAnswer(answers, qaEdits, ids.r2_branch);

  // Belt-and-braces: even with valid IDs, don't render an empty header if
  // every value resolved to blank (extremely defensive — shouldn't happen
  // when resolveComparisonIds returns non-null, but cheap to guard).
  if (!r1Brand && !r1Branch && !r2Brand && !r2Branch) return null;

  const left  = [r1Brand, r1Branch].filter(Boolean).join(' · ');
  const right = [r2Brand, r2Branch].filter(Boolean).join(' · ');

  if (variant === 'inline') {
    return (
      <div className={className} data-testid="comparison-header-inline">
        <p className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#94A3B8]">
          Comparison Report
        </p>
        <p className="text-sm font-semibold text-[#0F172A] mt-0.5">
          <span data-testid="comparison-restaurant-1">{left || '—'}</span>
          <span className="mx-2 text-xs font-normal text-[#94A3B8]">vs</span>
          <span data-testid="comparison-restaurant-2">{right || '—'}</span>
        </p>
      </div>
    );
  }

  // Card variant — used inside the report header card / response-detail header.
  return (
    <div
      className={`mb-3 pb-3 border-b border-dashed border-[#E2E8F0] ${className}`}
      data-testid="comparison-header"
    >
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#64748B]">
        Comparison Report
      </p>
      <p className="mt-1 text-base sm:text-lg text-[#0F172A] leading-snug">
        <span className="font-semibold" data-testid="comparison-restaurant-1">{left || '—'}</span>
        <span className="mx-2 text-sm font-normal text-[#94A3B8]">vs</span>
        <span className="font-semibold" data-testid="comparison-restaurant-2">{right || '—'}</span>
      </p>
    </div>
  );
}
